import type { QueryResultLike } from '@krp/client-runtime'

import { grantsOf, TESTING_SEARCH } from '../../access/model/grants'
import type { MyAccess } from '../../access/model/types'
import type { CommonCatalogPage } from '../../catalog/model/types'
import { confidencePercent } from '../../search/model/selectors'
import { actionView, viewOf } from '../../../shared/views'
import type { MutationResultLike } from '../../../shared/views'
import { testingPhotoUrl } from '../api/urls'
import type {
  CatalogPickVm,
  MutationStateVm,
  ShareVm,
  StateBadgeVm,
  TestingAccess,
  TestingCheck,
  TestingCheckVm,
  TestingHit,
  TestingHitVm,
  TestingLane,
  TestingLaneMetrics,
  TestingMean,
  TestingProportion,
  TestingRun,
  TestingRunDetail,
  TestingRunRowVm,
  TestingRunsVm,
  TestingRunState,
  TestingRunVm,
  TestPhoto,
  TestPhotoVm,
  TestProduct,
  TestProductDetail,
  TestProductDraft,
  TestProductsVm,
  TestProductVm,
  TextCheck,
  TextCheckVm,
  TextQueriesVm,
  TextQuery,
  TextQueryKind,
  TextRun,
  TextRunDetail,
  TextRunRowVm,
  TextRunsVm,
  TextRunVm,
  TextSearchStep,
} from './types'
import { isTestingLane, isTextQueryKind } from './types'

/**
 * vm вкладки «Тестирование» (RP-FB29): числа сервера словами. Каждый балл — с
 * числом кадров и 95-процентным интервалом: «72 %» без «n = 3» обманывает.
 * Формулы здесь не пересчитываются — их считает сервер по определениям стенда.
 */

export const LANE_TITLES: Readonly<Record<TestingLane, string>> = {
  all: 'После слияния — как в боевом поиске',
  visual: 'Только фото',
  text: 'Только текст на упаковке',
  barcode: 'Только штрихкод',
}

/** Короткие подписи дорожек для кнопок и таблиц. */
export const LANE_LABELS: Readonly<Record<TestingLane, string>> = {
  all: 'Все',
  visual: 'Фото',
  text: 'Текст',
  barcode: 'Штрихкод',
}

export const TAG_TITLES: Readonly<Record<string, string>> = {
  angle: 'Ракурс',
  quality: 'Качество',
  light: 'Свет',
  distance: 'Расстояние',
  occlusion: 'Перекрытие',
  packaging: 'Упаковка',
  barcode: 'Штрихкод на кадре',
}

/** Подсказки в полях пометок кадра: какие значения ждём. */
export const TAG_HINTS: Readonly<Record<string, string>> = {
  angle: 'спереди, сбоку, сверху',
  quality: 'чётко, размыто',
  light: 'день, лампа, блик',
  distance: 'близко, далеко',
  occlusion: 'рука, ценник',
  packaging: 'в плёнке, без',
  barcode: 'виден, не виден',
}

const MATCH_TITLES: Readonly<Record<string, string>> = { visual: 'фото', text: 'текст', barcode: 'штрихкод' }

const DELTA_TITLES: Readonly<Record<string, string>> = {
  top1: 'Первое место',
  top5: 'В первой пятёрке',
  mrr: 'Обратное место (MRR)',
  positionScore: 'Очко места',
  notFound: 'Не найден',
  expectedScore: 'Уверенность в нужном товаре',
  margin: 'Отрыв от соперника',
}

const NO_DATA = 'нет данных'
const fraction = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
/** Дорожки уверенности словами; неизвестная показывается как есть. */
const PART_LANE_LABELS: Readonly<Record<string, string>> = { visual: 'Фото', text: 'Текст', barcode: 'Штрихкод' }
/** Уверенность позиции для проверки кадра: «87%» и вклады дорожек («Фото +29, Текст +50»; нулевые не показываются). */
function confidenceText(hit: TestingHit): Pick<TestingHitVm, 'confidence' | 'parts'> {
  const percent = confidencePercent(hit.confidence)
  return {
    confidence: percent === null ? NO_DATA : `${percent}%`,
    parts: percent === null
      ? ''
      : hit.confidenceParts
          .map((part) => ({ part, points: Math.round(part.points * 100) }))
          .filter(({ points }) => points > 0)
          .map(({ part, points }) => `${PART_LANE_LABELS[part.lane] ?? part.lane} +${points}`)
          .join(', '),
  }
}
const signed = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'exceptZero' })
const when = new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' })

const percent = (value: number): string => `${Math.round(value * 100)} %`

export function whenText(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : when.format(date)
}

/** Доля: «72 %» и «95 %: 61–81 %, n = 50». */
export function shareOf(share: TestingProportion): ShareVm {
  if (share.value === null || share.n === 0) return { value: NO_DATA, detail: 'n = 0' }
  const interval = share.low === null || share.high === null ? '' : `95 %: ${Math.round(share.low * 100)}–${Math.round(share.high * 100)} %, `
  return { value: percent(share.value), detail: `${interval}n = ${share.n}` }
}

/** Среднее (очко места, MRR, сходство): дробью; интервала нет у одного наблюдения. */
export function meanOf(mean: TestingMean): ShareVm {
  if (mean.value === null || mean.n === 0) return { value: NO_DATA, detail: 'n = 0' }
  const interval = mean.low === null || mean.high === null ? '' : `95 %: ${fraction.format(mean.low)}–${fraction.format(mean.high)}, `
  return { value: fraction.format(mean.value), detail: `${interval}n = ${mean.n}` }
}

export function accessOf(access: QueryResultLike<MyAccess>): TestingAccess {
  const grants = grantsOf(access)
  if (!grants.known) return access.isError ? 'denied' : 'unknown'
  return grants.permissions.has(TESTING_SEARCH) ? 'allowed' : 'denied'
}

function stateOf(result: MutationResultLike<unknown> | null): MutationStateVm {
  return actionView(result)
}

/** Последняя по времени команда окна: отказ важнее хода, ход — покоя. */
export function lastCommand(results: readonly MutationResultLike<unknown>[]): MutationResultLike<unknown> | null {
  return results.find((result) => result.isError) ?? results.find((result) => result.isLoading) ?? null
}

function indexBadge(product: TestProduct): StateBadgeVm {
  return product.indexState === 'indexed'
    ? { tone: 'ok', text: 'В тестовом индексе' }
    : { tone: 'bad', text: `Не в индексе${product.indexError === null ? '' : `: ${product.indexError}`}` }
}

function sourceText(product: TestProduct): string | null {
  return product.source === null ? null : `Скопирован из товара каталога ${whenText(product.source.copiedAt)}`
}

/** Штрихкоды из поля: через запятую, пробел или с новой строки; повторы убираются. */
export function parseBarcodes(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).map((item) => item.trim()).filter((item) => item !== ''))]
}

export const draftOf = (product: TestProduct): TestProductDraft => ({
  name: product.name,
  sku: product.sku ?? '',
  barcodes: product.barcodes.join(', '),
})

export interface TestProductsVmInput {
  products: QueryResultLike<readonly TestProduct[]>
  catalog: QueryResultLike<CommonCatalogPage> | null
  command: MutationResultLike<unknown> | null
}

export function buildTestProductsVm(input: TestProductsVmInput): TestProductsVm {
  const query = viewOf(input.products, (rows) => rows.length === 0)
  const rows = (input.products.data ?? []).map((product) => ({
    id: product.id,
    name: product.name,
    sku: product.sku,
    barcodes: product.barcodes.length === 0 ? 'без штрихкода' : product.barcodes.join(', '),
    photos: `эталонов ${product.references}, кадров ${product.frames}`,
    index: indexBadge(product),
    sourceText: sourceText(product),
  }))
  const catalog: CatalogPickVm =
    input.catalog === null
      ? { query: null, items: [] }
      : {
          query: viewOf(input.catalog, (page) => page.items.length === 0),
          items: (input.catalog.data?.items ?? []).map((item) => ({ id: item.id, name: item.displayName, sku: item.sku })),
        }
  return { query, rows, catalog, action: stateOf(input.command) }
}

function photoVm(photo: TestPhoto): TestPhotoVm {
  const tags = Object.entries(photo.tags).map(([axis, value]) => `${TAG_TITLES[axis] ?? axis}: ${value}`)
  return {
    id: photo.id,
    url: testingPhotoUrl(photo.id),
    originText: photo.origin === 'source' ? 'из товара каталога' : 'добавлено вручную',
    tagsText: tags.length === 0 ? 'без пометок' : tags.join(', '),
  }
}

export interface TestProductVmInput {
  product: QueryResultLike<TestProductDetail>
  command: MutationResultLike<unknown> | null
}

export function buildTestProductVm(input: TestProductVmInput): TestProductVm {
  const query = viewOf(input.product, () => false)
  const detail = query.status === 'ready' ? (input.product.data ?? null) : null
  return {
    query,
    product: detail?.product ?? null,
    draft: detail === null ? null : draftOf(detail.product),
    index: detail === null ? null : indexBadge(detail.product),
    sourceText: detail === null ? null : sourceText(detail.product),
    references: (detail?.references ?? []).map(photoVm),
    sourcePhotosNote:
      detail !== null && detail.product.source !== null && !detail.references.some((photo) => photo.origin === 'source')
        ? 'Эталонов из товара каталога нет: либо в его карточках 1С и AQSI нет фото, либо их удалили здесь. «Обновить из источника» подтянет фото заново, если они есть; иначе добавьте эталон вручную.'
        : null,
    frames: (detail?.frames ?? []).map(photoVm),
    action: stateOf(input.command),
  }
}

/** Место ожидаемого товара словами: значок и текст, а не только цвет. */
function verdictOf(rank: number | null, expectedKnown: boolean): StateBadgeVm {
  if (!expectedKnown) return { tone: 'muted', text: 'Ожидаемый товар не задан' }
  if (rank === null) return { tone: 'bad', text: 'Нужный товар не найден' }
  if (rank === 1) return { tone: 'ok', text: 'Нужный товар на 1-м месте' }
  return { tone: 'busy', text: `Нужный товар на ${rank}-м месте` }
}

export function buildTestingCheckVm(result: MutationResultLike<TestingCheck>): TestingCheckVm {
  const action = stateOf(result)
  const check = result.isSuccess ? (result.data ?? null) : null
  if (check === null) return { busy: action.busy, error: action.error, recognized: null, latency: null, lanes: [] }
  const recognizedParts = [
    check.recognized?.text === null || check.recognized?.text === undefined || check.recognized.text === '' ? null : `текст: «${check.recognized.text}»`,
    check.recognized === null || check.recognized.barcodes.length === 0 ? null : `штрихкоды: ${check.recognized.barcodes.join(', ')}`,
  ].filter((part): part is string => part !== null)
  return {
    busy: action.busy,
    error: action.error,
    recognized: recognizedParts.length === 0 ? 'На кадре ничего не прочитано' : `Прочитано — ${recognizedParts.join('; ')}`,
    latency: `${Math.round(check.latencyMs)} мс`,
    lanes: check.lanes.map((lane) => ({
      lane: lane.lane,
      title: LANE_TITLES[lane.lane],
      verdict: verdictOf(lane.expectedRank, check.expectedProductId !== null),
      margin: lane.margin === null ? null : `Отрыв от соседа: ${signed.format(lane.margin)}`,
      hits: lane.hits.map((hit) => ({
        rank: hit.rank,
        name: hit.name,
        ...confidenceText(hit),
        matchedBy: hit.matchedBy.map((by) => MATCH_TITLES[by] ?? by).join(', '),
        expected: hit.productId === check.expectedProductId,
      })),
    })),
  }
}

export const RUN_STATES: Readonly<Record<TestingRunState, StateBadgeVm>> = {
  queued: { tone: 'busy', text: 'В очереди' },
  running: { tone: 'busy', text: 'Идёт' },
  done: { tone: 'ok', text: 'Готов' },
  failed: { tone: 'bad', text: 'Сбой' },
  cancelled: { tone: 'muted', text: 'Отменён' },
}

const isActive = (run: TestingRun): boolean => run.state === 'queued' || run.state === 'running'

function lanesText(lanes: string): string {
  return lanes === 'all' ? 'все дорожки и слияние' : `только ${(isTestingLane(lanes) ? LANE_LABELS[lanes] : lanes).toLowerCase()}`
}

/** Главная дорожка прогона: слияние, а у прогона одной дорожки — она сама. */
function mainLane(run: TestingRun): TestingLaneMetrics | null {
  const lanes = run.metrics?.lanes ?? []
  return lanes.find((lane) => lane.lane === 'all') ?? lanes[0] ?? null
}

function runTitle(run: TestingRun): string {
  return `Прогон ${whenText(run.createdAt)} · ${lanesText(run.lanes)}`
}

function rowOf(run: TestingRun): TestingRunRowVm {
  const main = mainLane(run)
  return {
    id: run.id,
    state: RUN_STATES[run.state],
    lanesText: lanesText(run.lanes),
    when: whenText(run.createdAt),
    progress: `${run.doneFrames} из ${run.totalFrames} кадров`,
    top1: main === null ? null : shareOf(main.top1),
    top5: main === null ? null : shareOf(main.top5),
    cancellable: isActive(run),
  }
}

export interface TestingRunsVmInput {
  runs: QueryResultLike<readonly TestingRun[]>
  /** Идёт ли замер поиска по тексту: очередь у замеров общая, сервер второй не примет. */
  otherActive?: boolean
  command: MutationResultLike<unknown> | null
}

const OWN_RUNNING = 'Замер уже идёт — дождитесь его или отмените'

function blockedBy(ownActive: boolean, otherActive: boolean, other: string): string | null {
  if (ownActive) return OWN_RUNNING
  return otherActive ? `Идёт замер ${other} — очередь замеров общая, дождитесь его` : null
}

export function buildTestingRunsVm(input: TestingRunsVmInput): TestingRunsVm {
  const runs = input.runs.data ?? []
  const latest = runs.find((run) => run.state === 'done' && run.metrics !== null) ?? null
  const main = latest === null ? null : mainLane(latest)
  return {
    query: viewOf(input.runs, (rows) => rows.length === 0),
    rows: runs.map(rowOf),
    latest:
      latest === null || main === null
        ? null
        : {
            id: latest.id,
            title: runTitle(latest),
            scores: [
              { label: 'Первое место', share: shareOf(main.top1) },
              { label: 'В первой пятёрке', share: shareOf(main.top5) },
              { label: 'Не найден', share: shareOf(main.notFound) },
              { label: 'Очко места', share: meanOf(main.positionScore) },
            ],
          },
    running: blockedBy(runs.some(isActive), input.otherActive ?? false, 'поиска по тексту'),
    action: stateOf(input.command),
  }
}

function deltaText(metric: string, value: number | null): string {
  if (value === null) return NO_DATA
  return metric === 'top1' || metric === 'top5' || metric === 'notFound' ? `${value > 0 ? '+' : ''}${Math.round(value * 100)} п.` : signed.format(value)
}

function valueText(metric: string, value: number | null): string {
  if (value === null) return NO_DATA
  return metric === 'top1' || metric === 'top5' || metric === 'notFound' ? percent(value) : fraction.format(value)
}

export interface TestingRunVmInput {
  run: QueryResultLike<TestingRunDetail>
  command: MutationResultLike<unknown> | null
}

export function buildTestingRunVm(input: TestingRunVmInput): TestingRunVm {
  const query = viewOf(input.run, () => false)
  const detail = query.status === 'ready' ? (input.run.data ?? null) : null
  const run = detail?.run ?? null
  const metrics = run?.metrics ?? null
  const stamp = run?.stamp ?? null
  return {
    query,
    title: run === null ? null : runTitle(run),
    state: run === null ? null : RUN_STATES[run.state],
    progress: run === null ? null : `${run.doneFrames} из ${run.totalFrames} кадров`,
    active: run !== null && isActive(run),
    error: run?.error ?? null,
    lanes: (metrics?.lanes ?? []).map((lane) => ({
      lane: lane.lane,
      title: LANE_TITLES[lane.lane],
      frames: lane.frames,
      cells: [
        { label: 'Первое место', share: shareOf(lane.top1) },
        { label: 'В тройке', share: shareOf(lane.top3) },
        { label: 'В пятёрке', share: shareOf(lane.top5) },
        { label: 'В десятке', share: shareOf(lane.top10) },
        { label: 'Не найден', share: shareOf(lane.notFound) },
        { label: 'Обратное место (MRR)', share: meanOf(lane.mrr) },
        { label: 'Очко места', share: meanOf(lane.positionScore) },
        { label: 'Уверенность в нужном товаре', share: meanOf(lane.expectedScore) },
        { label: 'Отрыв от соперника', share: meanOf(lane.margin) },
      ],
    })),
    // Сервер отдаёт товары худшими вперёд: с них начинается разбор.
    products: (metrics?.products ?? []).map((slice) => ({
      productId: slice.productId,
      name: slice.name,
      frames: slice.frames,
      top1: shareOf(slice.top1),
      top5: shareOf(slice.top5),
      position: meanOf(slice.positionScore),
    })),
    tags: (metrics?.tags ?? []).map((slice) => ({
      key: `${slice.axis}:${slice.value}`,
      axis: TAG_TITLES[slice.axis] ?? slice.axis,
      value: slice.value,
      frames: slice.frames,
      top1: shareOf(slice.top1),
      top5: shareOf(slice.top5),
    })),
    confusions: (metrics?.confusions ?? []).map((confusion) => ({
      key: `${confusion.expectedProductId}:${confusion.insteadProductId}`,
      expectedProductId: confusion.expectedProductId,
      text: `«${confusion.expectedName}» → первым «${confusion.insteadName === '' ? 'товар удалён' : confusion.insteadName}»`,
      count: confusion.count,
    })),
    latency:
      metrics === null || metrics.latency.medianMs === null
        ? null
        : `медиана ${Math.round(metrics.latency.medianMs)} мс, 95-й процентиль ${Math.round(metrics.latency.p95Ms ?? metrics.latency.medianMs)} мс, n = ${metrics.latency.n}`,
    errors: metrics?.errors ?? 0,
    stamp:
      stamp === null
        ? []
        : [
            { label: 'Модель признаков', value: stamp.encoderModel === null ? 'не известна' : `${stamp.encoderModel}${stamp.encoderDim === null ? '' : ` (${stamp.encoderDim})`}` },
            { label: 'Версия Platform', value: stamp.platformVersion },
            { label: 'Слияние', value: `k = ${stamp.rrfK}; веса: фото ${stamp.visualWeight}, текст ${stamp.textWeight}, штрихкод ${stamp.barcodeWeight}` },
            { label: 'Формула уверенности', value: stamp.confidenceFormula ?? 'прежний коэффициент (до RP-FB54)' },
            { label: 'Подготовка кадра', value: stamp.photoPrep === 'product-box-tight' ? 'плотная рамка товара вместе с фоном, одно уменьшение до 384, без потерь' : stamp.photoPrep === 'product-box' ? 'рамка товара с запасом вместе с фоном, без потерь (прежний режим)' : stamp.photoPrep === 'background-removed-vision' ? 'фон убран (Apple Vision), кадр обрезан, без потерь (прежний режим)' : stamp.photoPrep === 'background-removed-prepared' ? 'фон убран (u2net), кадр обрезан, без потерь' : stamp.photoPrep === 'background-removed-cropped' ? 'фон убран, кадр обрезан' : stamp.photoPrep === 'background-removed' ? 'фон убран' : 'кадр без обрезки' },
            { label: 'Тестовый каталог', value: `товаров ${stamp.products}, кадров ${stamp.frames}` },
          ],
    comparison:
      detail === null || detail.comparison === null
        ? null
        : {
            previousRunId: detail.comparison.previousRunId,
            note: detail.comparison.sameStamp ? null : 'Прошлый прогон посчитан другой моделью, версией или весами — разница не только в поиске',
            rows: detail.comparison.deltas.map((delta) => ({
              label: DELTA_TITLES[delta.metric] ?? delta.metric,
              previous: valueText(delta.metric, delta.previous),
              current: valueText(delta.metric, delta.current),
              delta: deltaText(delta.metric, delta.delta),
            })),
          },
    action: stateOf(input.command),
  }
}

// --- поиск по тексту (RP-FB34) ------------------------------------------------------------------------------------

export const TEXT_KIND_TITLES: Readonly<Record<TextQueryKind, string>> = {
  name: 'Название',
  part: 'Часть названия',
  typo: 'Опечатка',
  layout: 'Раскладка',
  sku: 'Артикул',
  barcode: 'Штрихкод',
  synonym: 'Синоним',
  other: 'Другое',
}

/** Чем закончился запрос: ступень номенклатуры или ответ службы. */
export const TEXT_MATCH_TITLES: Readonly<Record<string, string>> = {
  barcode: 'Штрихкод целиком',
  sku: 'Артикул целиком',
  all_words: 'Служба · все слова',
  some_words: 'Служба · часть слов',
  semantic: 'Служба · по смыслу',
  'not-found': 'Не найден',
  // Служба нашла, но не объяснила чем (другой профиль или старая версия) — сервер ставит ступень.
  service: 'Служба · без объяснения',
}

/** Совпадение строки выдачи — коротко, для списка. */
const HIT_MATCH_TITLES: Readonly<Record<string, string>> = {
  barcode: 'штрихкод целиком',
  sku: 'артикул целиком',
  all_words: 'все слова',
  some_words: 'часть слов',
  semantic: 'по смыслу',
}

const STEP_TEXTS: Readonly<Record<TextSearchStep, string>> = {
  barcode: 'Штрихкод целиком — найден в базе, служба не спрашивалась',
  sku: 'Артикул целиком — найден в базе, служба не спрашивалась',
  'barcode-like': 'Длинные цифры без совпадения в базе — выдача пуста, как в номенклатуре',
  service: 'Поиск по тексту — служба text-search',
  empty: 'Пустой запрос — искать нечего',
}

const kindTitle = (kind: string): string => isTextQueryKind(kind) ? TEXT_KIND_TITLES[kind] : kind
const matchTitle = (matchedBy: string | null): string => (matchedBy === null ? 'служба не объяснила' : (TEXT_MATCH_TITLES[matchedBy] ?? matchedBy))

export interface TextQueriesVmInput {
  queries: QueryResultLike<readonly TextQuery[]>
  products: QueryResultLike<readonly TestProduct[]>
  generated: { removed: number; created: number } | null
  command: MutationResultLike<unknown> | null
}

export function buildTextQueriesVm(input: TextQueriesVmInput): TextQueriesVm {
  const queries = input.queries.data ?? []
  const counts: Record<string, number> = {}
  for (const query of queries) counts[query.kind] = (counts[query.kind] ?? 0) + 1
  return {
    query: viewOf(input.queries, (rows) => rows.length === 0),
    rows: queries.map((query) => ({
      id: query.id,
      text: query.text,
      kind: query.kind,
      kindTitle: kindTitle(query.kind),
      originText: query.origin === 'generated' ? 'сгенерирован' : 'свой',
      productName: query.productName === '' ? 'товар удалён' : query.productName,
    })),
    products: (input.products.data ?? []).map((product) => ({ id: product.id, name: product.name })),
    counts,
    generated:
      input.generated === null
        ? null
        : `Создано запросов: ${input.generated.created}; прежние сгенерированные заменены (${input.generated.removed}), свои остались`,
    action: stateOf(input.command),
  }
}

/** Место ожидаемого товара и чем он нашёлся — значком и словами. */
function textVerdict(check: TextCheck): StateBadgeVm {
  if (check.expectedProductId === null) return { tone: 'muted', text: 'Ожидаемый товар не задан' }
  if (check.expectedRank === null) return { tone: 'bad', text: 'Нужный товар не найден' }
  const how = matchTitle(check.expectedMatchedBy)
  return check.expectedRank === 1
    ? { tone: 'ok', text: `Нужный товар на 1-м месте · ${how}` }
    : { tone: 'busy', text: `Нужный товар на ${check.expectedRank}-м месте · ${how}` }
}

/** Проверка и «Сохранить как запрос»: отказ сохранения важнее — он последний по времени. */
export function buildTextCheckVm(result: MutationResultLike<TextCheck>, save: MutationResultLike<unknown> | null = null): TextCheckVm {
  const action = stateOf(result)
  const error = (save === null ? null : stateOf(save).error) ?? action.error
  const check = result.isSuccess ? (result.data ?? null) : null
  if (check === null) return { busy: action.busy, error, verdict: null, step: null, latency: null, hits: [], checked: null }
  return {
    busy: action.busy,
    error,
    verdict: textVerdict(check),
    step: STEP_TEXTS[check.step],
    latency: `${Math.round(check.latencyMs)} мс`,
    hits: check.hits.map((hit) => ({
      rank: hit.rank,
      name: hit.name,
      sku: hit.sku,
      matchedBy: hit.matchedBy === null ? '' : (HIT_MATCH_TITLES[hit.matchedBy] ?? hit.matchedBy),
      expected: hit.productId === check.expectedProductId,
    })),
    checked: { query: check.query, expectedProductId: check.expectedProductId },
  }
}

const isTextActive = (run: TextRun): boolean => run.state === 'queued' || run.state === 'running'

const textRunTitle = (run: TextRun): string => `Замер ${whenText(run.createdAt)}`

const textProgress = (run: TextRun): string => `${run.doneQueries} из ${run.totalQueries} запросов`

function textRowOf(run: TextRun): TextRunRowVm {
  const overall = run.metrics?.overall ?? null
  return {
    id: run.id,
    state: RUN_STATES[run.state],
    when: whenText(run.createdAt),
    progress: textProgress(run),
    top1: overall === null ? null : shareOf(overall.top1),
    top5: overall === null ? null : shareOf(overall.top5),
    cancellable: isTextActive(run),
  }
}

export interface TextRunsVmInput {
  runs: QueryResultLike<readonly TextRun[]>
  /** Идёт ли замер поиска по фото: очередь у замеров общая. */
  otherActive?: boolean
  command: MutationResultLike<unknown> | null
}

export function buildTextRunsVm(input: TextRunsVmInput): TextRunsVm {
  const runs = input.runs.data ?? []
  const latest = runs.find((run) => run.state === 'done' && run.metrics !== null) ?? null
  const overall = latest?.metrics?.overall ?? null
  return {
    query: viewOf(input.runs, (rows) => rows.length === 0),
    rows: runs.map(textRowOf),
    latest:
      latest === null || overall === null
        ? null
        : {
            id: latest.id,
            title: textRunTitle(latest),
            scores: [
              { label: 'Первое место', share: shareOf(overall.top1) },
              { label: 'В первой пятёрке', share: shareOf(overall.top5) },
              { label: 'Не найден', share: shareOf(overall.notFound) },
              { label: 'Очко места', share: meanOf(overall.positionScore) },
            ],
          },
    running: blockedBy(runs.some(isTextActive), input.otherActive ?? false, 'поиска по фото'),
    action: stateOf(input.command),
  }
}

function placeOf(rank: number | null, error: string | null): StateBadgeVm {
  if (error !== null) return { tone: 'bad', text: `Сбой: ${error}` }
  if (rank === null) return { tone: 'bad', text: 'Не найден' }
  if (rank === 1) return { tone: 'ok', text: '1-е место' }
  return { tone: 'busy', text: `${rank}-е место` }
}

export interface TextRunVmInput {
  run: QueryResultLike<TextRunDetail>
  command: MutationResultLike<unknown> | null
}

export function buildTextRunVm(input: TextRunVmInput): TextRunVm {
  const query = viewOf(input.run, () => false)
  const detail = query.status === 'ready' ? (input.run.data ?? null) : null
  const run = detail?.run ?? null
  const metrics = run?.metrics ?? null
  const stamp = run?.stamp ?? null
  const overall = metrics?.overall ?? null
  return {
    query,
    title: run === null ? null : textRunTitle(run),
    state: run === null ? null : RUN_STATES[run.state],
    progress: run === null ? null : textProgress(run),
    active: run !== null && isTextActive(run),
    error: run?.error ?? null,
    scores:
      overall === null
        ? []
        : [
            { label: 'Первое место', share: shareOf(overall.top1) },
            { label: 'В тройке', share: shareOf(overall.top3) },
            { label: 'В пятёрке', share: shareOf(overall.top5) },
            { label: 'В десятке', share: shareOf(overall.top10) },
            { label: 'Не найден', share: shareOf(overall.notFound) },
            { label: 'Обратное место (MRR)', share: meanOf(overall.mrr) },
            { label: 'Очко места', share: meanOf(overall.positionScore) },
          ],
    kinds: (metrics?.kinds ?? []).map((slice) => ({
      kind: slice.kind,
      title: kindTitle(slice.kind),
      queries: slice.queries,
      top1: shareOf(slice.top1),
      top5: shareOf(slice.top5),
      notFound: shareOf(slice.notFound),
    })),
    matches: (metrics?.matches ?? []).map((match) => ({ key: match.matchedBy, title: matchTitle(match.matchedBy), queries: match.queries })),
    // Сервер отдаёт запросы худшими вперёд: с них начинается разбор.
    worst: (metrics?.worst ?? []).map((outcome) => ({
      queryId: outcome.queryId,
      text: outcome.text,
      kindTitle: kindTitle(outcome.kind),
      productName: outcome.productName === '' ? 'товар удалён' : outcome.productName,
      place: placeOf(outcome.rank, outcome.error),
      detail: [
        outcome.error === null ? matchTitle(outcome.matchedBy ?? 'not-found') : null,
        outcome.above.length === 0 ? null : `выше: ${outcome.above.map((name) => `«${name}»`).join(', ')}`,
      ]
        .filter((part): part is string => part !== null)
        .join(' · '),
    })),
    latency:
      metrics === null || metrics.latency.medianMs === null
        ? null
        : `медиана ${Math.round(metrics.latency.medianMs)} мс, 95-й процентиль ${Math.round(metrics.latency.p95Ms ?? metrics.latency.medianMs)} мс, n = ${metrics.latency.n}`,
    errors: metrics?.errors ?? 0,
    stamp:
      stamp === null
        ? []
        : [
            { label: 'Версия Platform', value: stamp.platformVersion },
            { label: 'Поколение индекса text-search', value: stamp.textGeneration ?? 'не известно' },
            { label: 'Тестовый каталог', value: `товаров ${stamp.products}, запросов ${stamp.queries}` },
          ],
    comparison:
      detail === null || detail.comparison === null
        ? null
        : {
            previousRunId: detail.comparison.previousRunId,
            note: detail.comparison.sameStamp ? null : 'Прошлый замер посчитан другой версией или другим индексом — разница не только в поиске',
            rows: detail.comparison.deltas.map((delta) => ({
              label: DELTA_TITLES[delta.metric] ?? delta.metric,
              previous: valueText(delta.metric, delta.previous),
              current: valueText(delta.metric, delta.current),
              delta: deltaText(delta.metric, delta.delta),
            })),
          },
    action: stateOf(input.command),
  }
}
